import {
    Container,
    ContainerSize,
    Heading,
    Table,
    TableColor,
    TableSize,
    Text,
    Wrapper,
} from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

/** Classic Bootstrap docs sample rows (#, First, Last, Handle), extended with a few more names. */
const SAMPLE_PEOPLE_ROWS = [
    { row: "1", first: "Mark", last: "Otto", handle: "@mdo" },
    { row: "2", first: "Jacob", last: "Thornton", handle: "@fat" },
    { row: "3", first: "John", last: "Doe", handle: "@social" },
    { row: "4", first: "Larry", last: "Bird", handle: "@twitter" },
    { row: "5", first: "Maria", last: "Gonzalez", handle: "@mgonz" }
] as const;

const SAMPLE_TEAM_ROWS = [
    { name: "Ada Lovelace", role: "Engineer" },
    { name: "Grace Hopper", role: "Engineer" },
    { name: "Alan Turing", role: "Research" },
    { name: "Katherine Johnson", role: "Analyst" },
    { name: "Tim Berners-Lee", role: "Fellow" }
] as const;

const RESPONSIVE_COL_COUNT = 9;
const RESPONSIVE_DATA_ROWS = 4;

const SamplePeopleThead = () => (
    <Table.Thead>
        <Table.Tr>
            <Table.Th scope="col">#</Table.Th>
            <Table.Th scope="col">First</Table.Th>
            <Table.Th scope="col">Last</Table.Th>
            <Table.Th scope="col">Handle</Table.Th>
        </Table.Tr>
    </Table.Thead>
);

const SamplePeopleTbody = () => (
    <Table.Tbody>
        {SAMPLE_PEOPLE_ROWS.map((p) => (
            <Table.Tr key={p.row}>
                <Table.Th scope="row">{p.row}</Table.Th>
                <Table.Td>{p.first}</Table.Td>
                <Table.Td>{p.last}</Table.Td>
                <Table.Td>{p.handle}</Table.Td>
            </Table.Tr>
        ))}
    </Table.Tbody>
);

type TablePageProps = {};

const TablePage = ({ }: TablePageProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Table
            </Heading>
            <Text className="mb-4">
                The <Text el="strong">Table</Text> component mirrors Bootstrap’s HTML table structure: use{' '}
                <Text el="code">Table</Text> as the root <Text el="code">&lt;table&gt;</Text> and compose rows with{' '}
                <Text el="code">Table.Thead</Text>, <Text el="code">Table.Tbody</Text>, <Text el="code">Table.Tr</Text>,{' '}
                <Text el="code">Table.Th</Text>, and <Text el="code">Table.Td</Text>. Optional props apply the same{' '}
                <Text el="code">table-*</Text> utility classes as in Bootstrap.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Basic example
            </Heading>
            <Text className="mb-4">
                Same shape as the{' '}
                <Wrapper
                    el="a"
                    href="https://getbootstrap.com/docs/5.3/content/tables/#overview"
                    className="link-primary"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap tables overview
                </Wrapper>
                : header plus several body rows so patterns like striping and hover read clearly.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Table>
                        <SamplePeopleThead />
                        <SamplePeopleTbody />
                    </Table>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {basicExampleCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Striped, bordered, and hover
            </Heading>
            <Text className="mb-4">
                Combine <Text el="code">striped</Text>, <Text el="code">bordered</Text>, and <Text el="code">hover</Text>{' '}
                on the root <Text el="code">Table</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Table striped bordered hover>
                        <SamplePeopleThead />
                        <SamplePeopleTbody />
                    </Table>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {stripedBorderedHoverCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Dark header
            </Heading>
            <Text className="mb-4">
                Pass <Text el="code">dark</Text> to <Text el="code">Table.Thead</Text> for <Text el="code">table-dark</Text> on the header.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Table bordered>
                        <Table.Thead dark>
                            <Table.Tr>
                                <Table.Th scope="col">Name</Table.Th>
                                <Table.Th scope="col">Role</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {SAMPLE_TEAM_ROWS.map((m) => (
                                <Table.Tr key={m.name}>
                                    <Table.Td>{m.name}</Table.Td>
                                    <Table.Td>{m.role}</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {darkHeaderCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Color and small size
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">color</Text> and <Text el="code">size</Text> on the root table, matching Bootstrap contextual tables.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <div className="d-flex flex-column gap-3">
                        <Table color={TableColor.Primary} size={TableSize.Small}>
                            <Table.Tbody>
                                <Table.Tr>
                                    <Table.Td>Primary row styling</Table.Td>
                                </Table.Tr>
                                <Table.Tr>
                                    <Table.Td>Second row in compact table</Table.Td>
                                </Table.Tr>
                            </Table.Tbody>
                        </Table>
                        <Table color={TableColor.Success}>
                            <Table.Tbody>
                                <Table.Tr>
                                    <Table.Td>Success row context</Table.Td>
                                </Table.Tr>
                                <Table.Tr>
                                    <Table.Td>Another success row</Table.Td>
                                </Table.Tr>
                            </Table.Tbody>
                        </Table>
                    </div>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {variantSmallCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Caption
            </Heading>
            <Text className="mb-4">
                Use <Text el="code">Table.Caption</Text> for an accessible description, like Bootstrap’s{' '}
                <Wrapper
                    el="a"
                    href="https://getbootstrap.com/docs/5.3/content/tables/#captions"
                    className="link-primary"
                    target="_blank"
                    rel="noreferrer"
                >
                    captions
                </Wrapper>{' '}
                example (here with <Text el="code">{'size={TableSize.Small}'}</Text>).
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Table size={TableSize.Small}>
                        <Table.Caption>List of users</Table.Caption>
                        <SamplePeopleThead />
                        <SamplePeopleTbody />
                    </Table>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {captionCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Responsive wrapper
            </Heading>
            <Text className="mb-4">
                Set <Text el="code">responsive</Text> to wrap the table in <Text el="code">table-responsive</Text> or a breakpoint-specific class.
                Wide tables with many columns mirror the{' '}
                <Wrapper
                    el="a"
                    href="https://getbootstrap.com/docs/5.3/content/tables/#responsive-tables"
                    className="link-primary"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap responsive tables
                </Wrapper>{' '}
                demo.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Table responsive="md" bordered>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th scope="col">#</Table.Th>
                                {Array.from({ length: RESPONSIVE_COL_COUNT }, (_, i) => (
                                    <Table.Th key={i} scope="col">Heading</Table.Th>
                                ))}
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {Array.from({ length: RESPONSIVE_DATA_ROWS }, (_, r) => (
                                <Table.Tr key={r}>
                                    <Table.Th scope="row">{r + 1}</Table.Th>
                                    {Array.from({ length: RESPONSIVE_COL_COUNT }, (_, c) => (
                                        <Table.Td key={c}>Cell</Table.Td>
                                    ))}
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {responsiveCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
};

export default TablePage;

const samplePeopleTheadTsx = `  <Table.Thead>
    <Table.Tr>
      <Table.Th scope="col">#</Table.Th>
      <Table.Th scope="col">First</Table.Th>
      <Table.Th scope="col">Last</Table.Th>
      <Table.Th scope="col">Handle</Table.Th>
    </Table.Tr>
  </Table.Thead>`;

const samplePeopleTbodyTsx = SAMPLE_PEOPLE_ROWS.map(
    (p) => `    <Table.Tr>
      <Table.Th scope="row">${p.row}</Table.Th>
      <Table.Td>${p.first}</Table.Td>
      <Table.Td>${p.last}</Table.Td>
      <Table.Td>${p.handle}</Table.Td>
    </Table.Tr>`
).join("\n");

const basicExampleCode = `<Table>
${samplePeopleTheadTsx}
  <Table.Tbody>
${samplePeopleTbodyTsx}
  </Table.Tbody>
</Table>`;

const stripedBorderedHoverCode = `<Table striped bordered hover>
${samplePeopleTheadTsx}
  <Table.Tbody>
${samplePeopleTbodyTsx}
  </Table.Tbody>
</Table>`;

const darkHeaderTbodyTsx = SAMPLE_TEAM_ROWS.map(
    (m) => `    <Table.Tr>
      <Table.Td>${m.name}</Table.Td>
      <Table.Td>${m.role}</Table.Td>
    </Table.Tr>`
).join("\n");

const darkHeaderCode = `<Table bordered>
  <Table.Thead dark>
    <Table.Tr>
      <Table.Th scope="col">Name</Table.Th>
      <Table.Th scope="col">Role</Table.Th>
    </Table.Tr>
  </Table.Thead>
  <Table.Tbody>
${darkHeaderTbodyTsx}
  </Table.Tbody>
</Table>`;

const variantSmallCode = `<Table color={TableColor.Primary} size={TableSize.Small}>
  <Table.Tbody>
    <Table.Tr>
      <Table.Td>Primary row styling</Table.Td>
    </Table.Tr>
    <Table.Tr>
      <Table.Td>Second row in compact table</Table.Td>
    </Table.Tr>
  </Table.Tbody>
</Table>

<Table color={TableColor.Success}>
  <Table.Tbody>
    <Table.Tr>
      <Table.Td>Success row context</Table.Td>
    </Table.Tr>
    <Table.Tr>
      <Table.Td>Another success row</Table.Td>
    </Table.Tr>
  </Table.Tbody>
</Table>`;

const captionCode = `<Table size={TableSize.Small}>
  <Table.Caption>List of users</Table.Caption>
${samplePeopleTheadTsx}
  <Table.Tbody>
${samplePeopleTbodyTsx}
  </Table.Tbody>
</Table>`;

const responsiveHeadTsx = `  <Table.Thead>
    <Table.Tr>
      <Table.Th scope="col">#</Table.Th>
${Array.from({ length: RESPONSIVE_COL_COUNT }, () => `      <Table.Th scope="col">Heading</Table.Th>`).join("\n")}
    </Table.Tr>
  </Table.Thead>`;

const responsiveBodyTsx = Array.from({ length: RESPONSIVE_DATA_ROWS }, (_, r) => {
    const n = r + 1;
    const cells = Array.from({ length: RESPONSIVE_COL_COUNT }, () => `      <Table.Td>Cell</Table.Td>`).join("\n");
    return `    <Table.Tr>
      <Table.Th scope="row">${n}</Table.Th>
${cells}
    </Table.Tr>`;
}).join("\n");

const responsiveCode = `<Table responsive="md" bordered>
${responsiveHeadTsx}
  <Table.Tbody>
${responsiveBodyTsx}
  </Table.Tbody>
</Table>`;
